import { Box, Typography } from '@mui/material';
import Seo from '../components/Seo';

export default function PrivacyPolicy() {
  return (
    <>
      <Seo
        path="/privacy-policy"
        title="Privacy Policy"
        description="BingePlay's privacy policy."
      />
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#f4f7fb',
          py: { xs: 4, md: 7 },
          px: 2,
        }}
      >
        <Box
          sx={{
            maxWidth: 980,
            mx: 'auto',
            backgroundColor: '#fff',
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 12px 40px rgba(0,0,0,0.08)',
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(90deg, #14D8CE 0%, #ff4d4f 100%)',
              color: '#fff',
              px: { xs: 3, md: 5 },
              py: { xs: 3, md: 4 },
            }}
          >
            <Typography sx={{ fontFamily: 'Rammetto One', fontSize: { xs: 24, md: 34 } }}>
              Privacy Policy
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mt: 1 }}>
              Last updated: April 29, 2026
            </Typography>
          </Box>

          <Box sx={{ px: { xs: 3, md: 5 }, py: { xs: 3, md: 5 }, color: '#1f2937' }}>
            <Typography sx={{ fontFamily: 'Poppins', mb: 2 }}>
              Funizzle (&quot;we&quot;, &quot;our&quot;, &quot;us&quot;) values your privacy. This
              Privacy Policy explains what information we collect, how we use it, how we share it,
              and your choices when you use our website, web app, and related services.
            </Typography>

            <Typography sx={{ fontFamily: 'Rammetto One', fontSize: 18, mb: 1.5, mt: 2 }}>
              1. Information We Collect
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              We may collect the following categories of data:
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              1. Account and profile data: Name, email address, phone number (optional in some
              flows), date of birth, gender, profile picture, and account role/permissions.
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              2. Authentication and security data: Login credentials (password is stored in hashed
              form), session/auth cookies, OTP verification data, and security-related logs.
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              3. Social and activity data: Follower/following relationships, support tickets, ticket
              replies, and in-app actions (such as creating/editing content).
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              4. Content you upload: Files and images you upload, including profile images, game or
              gamic files, thumbnails, and ticket attachments.
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              5. Payment and billing-related data: Payment method tokens, card metadata (for example
              brand, last 4 digits, expiry), Stripe customer IDs, and payment transaction
              references. We do not store full card numbers or CVV on our servers.
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              6. Device and notification data: Device push identifier (such as OneSignal player ID)
              for sending service notifications.
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 2 }}>
              7. Location data: Latitude/longitude (where provided), and derived location details
              (country/state/city) used for features such as targeting and relevance.
            </Typography>

            <Typography sx={{ fontFamily: 'Rammetto One', fontSize: 18, mb: 1.5, mt: 2 }}>
              2. How We Use Your Information
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              We use your information to:
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              1. Create and manage user accounts
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              2. Authenticate users and secure sessions
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              3. Verify email/OTP and recover accounts
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              4. Enable uploads, publishing, and content management
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              5. Process payments and wallet/billing workflows
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              6. Provide customer support through ticketing
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              7. Send transactional and service notifications
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              8. Improve platform functionality, moderation, and analytics
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 2 }}>
              9. Comply with legal obligations and enforce platform policies
            </Typography>

            <Typography sx={{ fontFamily: 'Rammetto One', fontSize: 18, mb: 1.5, mt: 2 }}>
              3. Cookies and Similar Technologies
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 2 }}>
              We use cookies and local storage for login/session continuity and user experience.
              This includes secure, HTTP-only authentication cookies and client-side
              preferences/profile caching. You can control cookies through your browser settings,
              but disabling essential cookies may affect core functionality (such as login and
              protected areas).
            </Typography>

            <Typography sx={{ fontFamily: 'Rammetto One', fontSize: 18, mb: 1.5, mt: 2 }}>
              4. How We Share Information
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              We do not sell personal information.
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              We may share data with trusted service providers only to operate the platform,
              including:
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              1. Payment processing providers (for billing flows)
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              2. Cloud storage/file delivery providers (for uploaded files)
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              3. Email delivery providers (verification, OTP, notices)
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              4. Notification providers (push notifications)
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 2 }}>
              5. Infrastructure/security service providers as needed. We may also disclose
              information where required by law or to protect rights, safety, and platform
              integrity.
            </Typography>

            <Typography sx={{ fontFamily: 'Rammetto One', fontSize: 18, mb: 1.5, mt: 2 }}>
              5. Data Retention
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 2 }}>
              We retain personal data for as long as needed to provide services and meet
              legal/business requirements. If an account is marked deleted/deactivated, some data
              may be retained temporarily for security, fraud prevention, dispute handling,
              compliance, or backup integrity. Where applicable, scheduled deletion/cleanup may run
              after a retention window.
            </Typography>

            <Typography sx={{ fontFamily: 'Rammetto One', fontSize: 18, mb: 1.5, mt: 2 }}>
              6. Data Security
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 2 }}>
              We apply technical and organizational safeguards, including access controls, hashed
              passwords, secure transport practices, and restricted processing. However, no method
              of transmission or storage is 100% secure. We continuously work to improve security
              protections.
            </Typography>

            <Typography sx={{ fontFamily: 'Rammetto One', fontSize: 18, mb: 1.5, mt: 2 }}>
              7. Your Rights and Choices
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              Depending on your location and applicable laws, you may have rights to:
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              1. Access your personal data
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              2. Correct/update inaccurate data
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              3. Request deletion of your data
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              4. Object to or restrict certain processing
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 2 }}>
              5. Request information about how your data is used. You may also update profile fields
              and account settings directly in-app where available.
            </Typography>

            <Typography sx={{ fontFamily: 'Rammetto One', fontSize: 18, mb: 1.5, mt: 2 }}>
              8. Children&apos;s Privacy
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 2 }}>
              Our services are not intended for children under the age required by applicable law in
              your region. If we become aware that we collected data unlawfully from a child, we
              will take appropriate steps to remove it.
            </Typography>

            <Typography sx={{ fontFamily: 'Rammetto One', fontSize: 18, mb: 1.5, mt: 2 }}>
              9. International Data Transfers
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 2 }}>
              Your data may be processed or stored in jurisdictions different from your own. Where
              required, we use appropriate safeguards for cross-border data handling.
            </Typography>

            <Typography sx={{ fontFamily: 'Rammetto One', fontSize: 18, mb: 1.5, mt: 2 }}>
              10. Changes to This Privacy Policy
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 2 }}>
              We may update this policy from time to time. When we do, we will revise the Last
              Updated date. Continued use of the service after updates means you accept the revised
              policy.
            </Typography>

            <Typography sx={{ fontFamily: 'Rammetto One', fontSize: 18, mb: 1.5, mt: 2 }}>
              11. Contact Us
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', fontWeight: 600 }}>
              For privacy-related questions, requests, or complaints: support@funizzle.com
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
}
